// BUILD-046 correction cycle 1bb88b3e (stage B2a, item N1): what a view's list SHOWS, from what it has loaded - in one place, for Mine, Shared and Team.
//
// The order is fixed and the same in all three views:
//   1. the view's own client-side filter (Team: the department; Mine: the quick filter is applied by the page before this, as it always was);
//   2. the search of the toolbar (viewSearchModel.ts) over what is left;
//   3. the sort, the chosen mode when the view offers it, else Recommended: Recommended and the column modes (Task, Assigned to, Priority, Task due date,
//      Status) by taskOrdering.ts (Recommended on the corrected Phoenix date buckets, reconcile pass of 2026-10-20); Oldest, Newest and Project by the legacy
//      sort (sortModel.ts), unchanged. The legacy declarations remain the byte-identical reference for those three, for the four legacy quick filters and for
//      the feature-off page.
// The count the view shows ("loaded") is the number of tasks after step 1, i.e. exactly the tasks the filters leave in scope; the search narrows the
// ROWS, never the count, and says so (`searchScope`): "Searching the N loaded tasks; load more to search all." while more pages exist, "M of N tasks
// match." once everything is loaded. Nothing here reads a task the caller was not given.
//
// Pure module. Runtime imports: the department filter, the search and the sort (all pure). Erasable TypeScript only.

import { filterByDepartment } from './oversightViewModel';
// BUILD-046 J1a: the Customer / Job filter (pure), applied right after the department filter.
import { filterByJob, jobFilterActive } from './jobFilterModel';
import type { JobFilter } from './jobFilterModel';
// BUILD-046 J1a (Customer association): the Customer filter (pure), applied beside the Job filter, right after the department filter. The two are independent narrowings.
import { filterByCustomer, customerFilterActive } from './customerFilterModel';
import type { CustomerFilter } from './customerFilterModel';
// BUILD-046 1b-ii: the "Assigned to" filter (pure), applied right after the Customer / Job and Customer filters. It keeps the loaded rows whose Assigned to cell shows the chosen person, whole and in order.
import { assigneeFilterActive, filterByAssignee, peopleOfTask } from './assigneeFilterModel';
import type { AssigneeFilter } from './assigneeFilterModel';
import { searchLoaded, searchScopeText } from './viewSearchModel';
import type { SearchableTask } from './viewSearchModel';
import { sortItems } from './sortModel';
import type { SortMode, SortView, SortableItem } from './sortModel';
// Finish-b: the modes a column stands for (Task, Assigned to, Priority, Task due date, Status) are ordered by taskOrdering.ts; so is Recommended since the reconcile
// pass (corrected Phoenix date buckets). Oldest, Newest and Project stay with the legacy `sortItems` above.
import { effectivePageSortMode, isColumnSortMode, MODE_ORDER } from './pageSortModel';
import type { PageSortMode } from './pageSortModel';
import { recommendedOrder, sortTasks } from './taskOrdering';
// BUILD-046 section C: "Needs attention" is its own order (attentionModel.ts), decided on the same Phoenix day.
import { attentionOrder } from './attentionModel';
// BUILD-046 section D: a preset (or the Sort order editor) may order by up to three ordered rules; they compose the same column orders (multiSort.ts) and replace the single mode while present.
import { applySortRules } from './multiSort';
import { normalizeRules } from './presetsModel';
import type { SortRule } from './presetsModel';
import { phoenixToday } from './dateLabels';

export interface DeriveOptions {
  /** The toolbar's query for THIS view. */
  query: string;
  /** The sort the Caller chose (any value). */
  sort: unknown;
  view: SortView;
  /** Team only: keep rows carrying exactly this department. Empty keeps every row. */
  department?: string;
  /** Keep the rows of one Customer / Job, or the rows with no job (BUILD-046 J1a). Absent, or `any`, keeps every row. */
  job?: JobFilter;
  /** Keep the rows of one Customer, or the rows with no customer (BUILD-046 J1a, Customer association). Absent, or `any`, keeps every row. Independent of `job`. */
  customer?: CustomerFilter;
  /** Keep the rows whose Assigned to cell shows this person (BUILD-046 1b-ii; Team / Company). Absent, or `any`, keeps every row. A row is kept whole and rows keep their order. */
  assignee?: AssigneeFilter;
  /** True when the list is whole (it has no further page): the search then covers everything the Caller may see in this view. */
  complete: boolean;
  /** The Phoenix day (YYYY-MM-DD) Recommended decides "overdue" and "due today" by. Defaults to today in Phoenix; the page passes the one it renders with. */
  today?: string;
  /** Ordered sort rules (BUILD-046 section D). Present and non-empty: they decide the order and the single `sort` mode is not used. Keys the view has no column for are dropped. */
  rules?: readonly SortRule[];
}

export interface DerivedList<T> {
  /** The rows to draw, searched and sorted. */
  rows: T[];
  /** The tasks in scope after the view's own filter (what the view's count counts). */
  inScope: readonly T[];
  /** The sort mode actually applied. */
  sort: PageSortMode;
  /** The truthful search sentence, or null while the query is blank. */
  searchScope: string | null;
  /** The query narrowed the rows. */
  searching: boolean;
  /** Something (a department or a query) narrows the list: an empty result is "nothing matches", not "nothing here". */
  narrowed: boolean;
}

export function deriveViewList<T extends SortableItem & SearchableTask & { department?: string }>(items: readonly T[], options: DeriveOptions): DerivedList<T> {
  const department = options.department ?? '';
  const byDepartment = department === '' ? items : filterByDepartment(items, department);
  const byJob = options.job === undefined ? byDepartment : filterByJob(byDepartment, options.job);
  const byCustomer = options.customer === undefined ? byJob : filterByCustomer(byJob, options.customer);
  const inScope = options.assignee === undefined ? byCustomer : filterByAssignee(byCustomer, options.assignee, peopleOfTask);
  const result = searchLoaded(inScope, options.query, { complete: options.complete });
  const sort = effectivePageSortMode(options.sort, options.view, inScope);
  // Recommended and the column modes are ordered by taskOrdering (a reverse request is a real reverse, never Recommended; Recommended buckets by the Phoenix day);
  // Oldest, Newest and Project by the legacy sort, unchanged.
  const rules = options.rules === undefined ? [] : normalizeRules(options.view, options.rules);
  const rows =
    rules.length > 0
      ? applySortRules(result.visible, rules)
      : sort === 'recommended'
      ? recommendedOrder(result.visible, options.today ?? phoenixToday())
      : sort === 'attention'
        ? attentionOrder(result.visible, options.today ?? phoenixToday())
      : isColumnSortMode(sort)
        ? sortTasks(result.visible, MODE_ORDER[sort].key, MODE_ORDER[sort].direction)
        : sortItems([...result.visible], sort as SortMode);
  return {
    rows,
    inScope,
    sort,
    searchScope: searchScopeText(result),
    searching: result.active,
    narrowed: result.active || department !== '' || (options.job !== undefined && jobFilterActive(options.job)) || (options.customer !== undefined && customerFilterActive(options.customer)) || (options.assignee !== undefined && assigneeFilterActive(options.assignee)),
  };
}
